import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ControlledTextField } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import type { WorkOrderObjectSelectionData } from 'gql/graphql';
import { ContractObjectStatusEnum } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import type { IWorkOrderObjectEdit } from 'pages/Project/Common/WorkOrderStructure/types';
import { cnMixSpace, Informer, IconWarning } from 'uikit';
import { requiredBaseRule } from 'utils/constants';

import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import Style from './UerWorkOrderObjectEditComponent.module.css';

export const UerWorkOrderObjectEditComponent: React.FC<IWorkOrderObjectEdit<WorkOrderObjectSelectionData>> = (
    props,
) => {
    const {
        useWorkOrderEditObjectDataSource,
        objectToEdit,
        workOrderOrChangeId,
        contractId,
        constructionTypeId,
        onClose,
        parentObjectToAppend,
        checkedIds,
        canEditObjectCode,
    } = props;
    const isContractObject = !!objectToEdit && objectToEdit.status !== ContractObjectStatusEnum.NewRecord;

    const { control, formState, handleSubmit, watch, setError, clearErrors } = useForm({
        defaultValues:
            objectToEdit &&
            ({
                name: objectToEdit?.name,
                rationale: objectToEdit?.rationale ?? '',
                codeWBS: objectToEdit?.codeWBS ?? '',
            } as any),
    });

    const btnConfirmTitle = useMemo(
        () => `${objectToEdit ? 'Редактирование' : 'Создание'} ${parentObjectToAppend ? 'подобъекта' : 'объекта'}`,
        [objectToEdit, parentObjectToAppend],
    );
    const btnConfirmLabel = objectToEdit ? 'Сохранить' : 'Создать';

    const onSuccess = useCallback(() => {
        onClose(true, objectToEdit?.id);
    }, [objectToEdit?.id, onClose]);

    const { doCreateRequest, doUpdateRequest, btnConfirmDisabled } =
        useWorkOrderEditObjectDataSource?.({
            formState,
            setError,
            clearErrors,
            watch,
            constructionTypeId,
            contractId,
            workOrderOrChangeId,
            onCreateSuccess: onSuccess,
            onUpdateSuccess: onSuccess,
            objectId: objectToEdit?.id,
            initialCode: objectToEdit?.codeWBS,
            isContractObject,
            parentObjectToAppend,
            checkedIds,
        }) || {};

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onClose,
        });

    const onModalClose = useCallback(() => {
        if (formState?.isDirty) {
            handleOpenConfirmationDialog();
        } else {
            onClose();
        }
    }, [formState?.isDirty, handleOpenConfirmationDialog, onClose]);

    const onSubmitDialog = useCallback(
        (formData) => {
            if (objectToEdit) {
                doUpdateRequest?.(
                    isContractObject
                        ? { ...formData, name: objectToEdit.name, rationale: objectToEdit.rationale ?? '' }
                        : formData,
                );
            } else {
                doCreateRequest?.(formData);
            }
        },
        [doCreateRequest, doUpdateRequest, isContractObject, objectToEdit],
    );

    return (
        <ModalDialog
            title={btnConfirmTitle}
            isOpen
            onClose={onModalClose}
            onConfirm={handleSubmit(onSubmitDialog)}
            btnConfirmLabel={btnConfirmLabel}
            btnConfirmDisabled={btnConfirmDisabled}
            className={Style.modal}
            isOnClickOutsideDisable
            withTitleCloseButton
        >
            <div className={cnMixSpace({ m: 'xl' })}>
                <Informer
                    view="filled"
                    status="warning"
                    label={`${objectToEdit ? 'Редактирование' : 'Добавление нового'} ${
                        parentObjectToAppend ? 'подобъекта' : 'объекта'
                    } обновит структуру Договора`}
                    className={cnMixSpace({ mB: 'm' })}
                    icon={IconWarning}
                    size="s"
                />
                <form>
                    <ControlledTextField
                        name="name"
                        label="Наименование"
                        control={control}
                        type="text"
                        directionTooltip="upCenter"
                        tooltipSize="l"
                        view="default"
                        labelPosition="top"
                        className={cnMixSpace({ mB: 'm' })}
                        required
                        rules={requiredBaseRule}
                        disabled={isContractObject}
                    />
                    <ControlledTextField
                        name="codeWBS"
                        label="Код"
                        control={control}
                        type="text"
                        directionTooltip="upCenter"
                        tooltipSize="l"
                        view="default"
                        labelPosition="top"
                        className={cnMixSpace({ mB: 'm' })}
                        disabled={!canEditObjectCode}
                    />
                    {!isContractObject && (
                        <ControlledTextField
                            labelPosition="top"
                            name="rationale"
                            label="Обоснование"
                            placeholder="Введите"
                            control={control}
                            type="textarea"
                            cols={50}
                            rows={5}
                            required
                            rules={requiredBaseRule}
                        />
                    )}
                </form>
            </div>
            <ConfirmationDialog
                textHeader="ВВОД ДАННЫХ"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </ModalDialog>
    );
};

UerWorkOrderObjectEditComponent.displayName = 'UerWorkOrderObjectEditComponent';
